import { Button, Col, Form, Row, Select, Table, Tooltip, message } from "antd";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { DatePicker as DatePickerJalali, JalaliLocaleListener } from "antd-jalali";
import { Excel } from "antd-table-saveas-excel";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAPIPublic, useDealer } from "../../../../tools/api";
import { complexDigitToman, convertToGregorian, convertToJalaliWithoutTime } from "../../../../tools/tools";
import { NotFound } from "../../../../sources/svg/svg";
import Loading from "../../../auxiliary/loader/loading";
import styles from "./submitedRequest.module.scss";

const ASSIGNED_STATUS_ID = 3;

const KIT_COLUMNS = [
	{ title: "شماره کیت", dataIndex: "kitNo", key: "kitNo", width: 150 },
	{ title: "نوع مخزن", dataIndex: "poolType", key: "poolType", width: 180 },
	{ title: "وضعیت", dataIndex: "requestStatus", key: "requestStatus", width: 150 },
];

const SubmitedRequest = () => {
	const [frmSearch] = Form.useForm();
	const [messageApi, contextHolder] = message.useMessage();

	const { queryICCIDRequestStatus } = useAPIPublic();
	const { queryRegisterKitRequest, queryKitAssignedToUser } = useDealer();
	const { _currentUser } = useSelector((state) => state?.another);

	const [statusList, setStatusList] = useState([]);
	const [tableData, setTableData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [downloadingId, setDownloadingId] = useState(null);

	// state current page show Records
	const [currentPage, setCurrentPage] = useState(0);
	// state totalRecord
	const [totalRecord, setTotalRecord] = useState(0);

	useEffect(() => {
		const loadStatusList = async () => {
			const response = await queryICCIDRequestStatus();
			if (response) {
				setStatusList(response);
			}
		};
		loadStatusList();
	}, []);

	const handleSearch = async () => {
		setIsLoading(true);
		try {
			const values = frmSearch.getFieldsValue();

			const payload = {
				searchConfigModel: {
					sort: "",
					filter: "",
					entriesPerPage: 10,
					page: currentPage === 0 ? 0 : currentPage - 1,
					totalRowCount: 0,
				},
				userName: _currentUser?.username,
			};

			if (values?.status !== undefined && values?.status !== null) {
				payload.requestStatus = values.status;
			}

			if (values?.fromdate) {
				payload.fromReqDate = convertToGregorian(values.fromdate.format("YYYY-MM-DD"), "-", "-");
			}

			if (values?.todate) {
				payload.toReqDate = convertToGregorian(values.todate.format("YYYY-MM-DD"), "-", "-");
			}

			const response = await queryRegisterKitRequest(payload);

			if (response) {
				setTableData(response?.Result || []);
				setTotalRecord(response?.searchconfigmodel?.totalrowcount || 0);
			}
		} catch (error) {
			console.error("Failed to load submitted requests:", error);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		handleSearch();
	}, [currentPage]);

	// دریافت لیست کیت‌های تخصیص‌یافته‌ی یه درخواست و خروجی اکسل (با ساپورت فارسی)
	const handleDownload = async (requestId) => {
		if (!requestId) return;
		setDownloadingId(requestId);
		try {
			const kits = (await queryKitAssignedToUser({ iccidRequestId: requestId })) || [];
			if (kits.length > 0) {
				const excel = new Excel();
				excel
					.addSheet("کیت‌های تخصیص‌یافته")
					.addColumns(KIT_COLUMNS)
					.addDataSource(
						kits.map((item, key) => ({
							key,
							kitNo: item.kitNo,
							poolType: item.poolType,
							requestStatus: item.requestStatus,
						})),
						{ str2Percent: true },
					)
					.saveAs("kit-request-assigned-kits.xlsx");
			} else {
				messageApi.open({ type: "error", content: "کیتی برای این درخواست یافت نشد." });
			}
		} catch (error) {
			console.error("Failed to download assigned kits:", error);
			messageApi.open({ type: "error", content: "خروجی اکسل ساخته نشد. دوباره تلاش کنید." });
		}
		setDownloadingId(null);
	};

	const columns = [
		{
			title: "تاریخ درخواست",
			dataIndex: "requestDate",
			key: "requestDate",
			width: 150,
		},
		{
			title: "تعداد کیت",
			dataIndex: "requestedQuantity",
			key: "requestedQuantity",
			width: 120,
		},
		{
			title: "مبلغ",
			dataIndex: "totalAmount",
			key: "totalAmount",
			width: 150,
		},
		{
			title: "نوع مخزن",
			dataIndex: "poolType",
			key: "poolType",
			width: 180,
		},
		{
			title: "وضعیت",
			dataIndex: "requestStatus",
			key: "requestStatus",
			width: 150,
		},
		{
			title: "توضیحات",
			dataIndex: "description",
			key: "description",
			width: 120,
		},
		{
			title: "عملیات",
			dataIndex: "action",
			key: "action",
			width: 150,
		},
	];

	const dataSource = tableData.map((item, key) => ({
		key,
		requestDate: convertToJalaliWithoutTime(item.requestDate),
		requestedQuantity: item.requestedQuantity,
		totalAmount:
			item.totalAmount > 0 ? (
				<>
					<span dir="ltr">{complexDigitToman(item.totalAmount)}</span> تومان
				</>
			) : (
				"-"
			),
		poolType: item.poolType,
		requestStatus: item.requestStatus,
		description: item.description ? (
			<Tooltip title={item.description}>
				<InfoCircleOutlined /> توضیحات...
			</Tooltip>
		) : (
			"-"
		),
		action:
			item.requestStatusId === ASSIGNED_STATUS_ID ? (
				<Button className="btn-normal" loading={downloadingId === item.requestId} onClick={() => handleDownload(item.requestId)}>
					دانلود
				</Button>
			) : (
				"-"
			),
	}));

	return (
		<>
			{contextHolder}
			<div className="holder-form-filter">
				<Form form={frmSearch} layout="vertical" onFinish={handleSearch}>
					<Row gutter={16} align="bottom" className={styles.filterRow}>
						<Col>
							<JalaliLocaleListener />
							<Form.Item className="input-normal" name="fromdate" label="از تاریخ">
								<DatePickerJalali format="YYYY/MM/DD" placeholder="از تاریخ" />
							</Form.Item>
						</Col>
						<Col>
							<Form.Item className="input-normal" name="todate" label="تا تاریخ">
								<DatePickerJalali format="YYYY/MM/DD" placeholder="تا تاریخ" />
							</Form.Item>
						</Col>
						<Col>
							<Form.Item className="input-normal" name="status" label="وضعیت">
								<Select placeholder="وضعیت" allowClear>
									{statusList.map((item) => (
										<Select.Option key={item.value} value={item.value}>
											{item.name}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col>
							<Form.Item label="&nbsp;">
								<Button htmlType="submit" className="btn-normal btn-aptel" icon={<SearchOutlined />} />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</div>

			<div className="holder-table">
				<Table
					className="tbl-normal"
					columns={columns}
					locale={{
						emptyText: <NotFound text={"رکوردی یافت نشد."} />,
					}}
					pagination={{
						className: "page-pagination",
						onChange: (page) => {
							setCurrentPage(page);
						},
						pageSize: 10,
						align: "start",
						current: currentPage === 0 ? 1 : currentPage,
						total: totalRecord,
						showSizeChanger: false,
					}}
					scroll={{ y: "100%" }}
					dataSource={dataSource}
				/>
			</div>

			<Loading isOpen={isLoading} />
		</>
	);
};

export default SubmitedRequest;
